import { useEffect, useState } from "react";
import { Sparkles, X, Compass, RefreshCw } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ListingCard } from "@/components/ListingCard";
import { api, errorMessage } from "@/lib/api";
import { pollWhileBuilding, SEARCH_TIMEOUT } from "@/lib/poll";

export function QueryResults({ city, q, onOpen, onClose }) {
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setRes(null); setError(""); setTimedOut(false);
    return pollWhileBuilding(() => api.get("/search/query", { params: { city, q }, timeout: SEARCH_TIMEOUT }).then((r) => r.data), {
      interval: 3000,
      maxMs: 3 * 60 * 1000,
      onData: (d) => { setRes(d); setRetrying(false); },
      onRetry: () => setRetrying(true),
      onTimeout: () => setTimedOut(true),
      onError: (e) => { setRetrying(false); setError(errorMessage(e)); },
    });
  }, [city, q, attempt]);

  const loading = !error && !timedOut && (!res || res.status === "building");

  return (
    <section data-testid="query-results-section" className="mb-10 rounded-3xl border border-[#00E5A3]/30 bg-[#00E5A3]/5 p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-base md:text-lg font-bold flex items-center gap-2"><Sparkles className="h-5 w-5 text-[#00E5A3]" /> Results for “{q}” in {city}</h2>
        <button data-testid="query-results-close" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button>
      </div>
      {loading && (
        <div data-testid="query-loading">
          <p className="text-sm text-[#00E5A3] mb-4 flex items-center gap-2"><Compass className="h-4 w-4 animate-spin" /> {retrying ? <span data-testid="query-retrying" className="text-[#FFB800]">Weak signal — retrying…</span> : `Searching everything in ${city}… this takes about 15 seconds`}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">{[0, 1, 2, 3].map((k) => <Skeleton key={k} className="h-72 rounded-2xl bg-[#151C2C]" />)}</div>
        </div>
      )}
      {error && <p data-testid="query-error" className="text-sm text-[#FF7A7A]">{error}</p>}
      {timedOut && res?.status !== "ready" && (
        <p data-testid="query-timeout" className="text-sm text-[#FFB800] flex flex-wrap items-center gap-3">
          This is taking longer than usual.
          <button data-testid="query-timeout-retry" onClick={() => setAttempt((a) => a + 1)} className="underline font-semibold">Try again</button>
        </p>
      )}
      {res?.stale && (
        <p data-testid="query-stale-note" className="text-xs text-slate-400 mb-4 flex items-center gap-2">
          {res.refreshing ? <><RefreshCw className="h-3.5 w-3.5 animate-spin text-[#00E5A3]" /> Showing yesterday's results — today's are on the way…</> : "Showing yesterday's results — today's couldn't be fetched yet."}
        </p>
      )}
      {res?.status === "ready" && res.listings.length === 0 && (
        <p data-testid="query-no-results" className="text-sm text-slate-400">We couldn't find anything matching “{q}” here. Try different words or a nearby bigger city.</p>
      )}
      {res?.status === "ready" && res.listings.length > 0 && (
        <div data-testid="query-results-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {res.listings.map((item, i) => <ListingCard key={item.id} item={item} index={i} onOpen={onOpen} />)}
        </div>
      )}
    </section>
  );
}
